import React, { useRef, useState, useCallback, useContext, useDebugValue } from 'react';
import { useLayoutEffectWithoutSSRComplaining } from '@wix/da-hooks/pkg/useLayoutEffectWithoutSSRComplaining';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { BiEvent } from '@wix/da-bi/pkg/events';
import { DropdownDebugContext } from '../DropdownStateManager/DropdownStateContext';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';

export interface UseDropdownStateOptions {
  disabled?: boolean;
  openOnHover?: boolean;
  closeOnLeave?: boolean;
  disableKeyboardEvents?: boolean;
  /** Data for a BI event that will only fire when the dropdown is opened. */
  openBiData?: BiEvent;
  onOpen?: () => void;
  /** Data for a BI event that will only fire when the dropdown is closed. */
  closeBiData?: BiEvent;
  onClose?: () => void;
  onToggle?: (isOpen: boolean) => void;
  /** When using this inside a form with virtual keyboard its useful to wait for screen to settle before opening */
  mobileOpenDelay?: boolean;
}

export function useDropdownState(
  menuRef: React.MutableRefObject<HTMLElement | null>,
  toggleRef: React.MutableRefObject<HTMLElement | null>,
  {
    disabled,
    openOnHover,
    closeOnLeave = true,
    openBiData,
    onOpen,
    closeBiData,
    onClose,
    onToggle,
    mobileOpenDelay,
  }: UseDropdownStateOptions
) {
  const logBiEvent = useBiLogger();
  const { forceOpen } = useContext(DropdownDebugContext);
  const [isOpen, setIsOpen] = useState(forceOpen);
  const wasOpen = useRef(false);
  const focusWasInDropdown = useRef(false);
  const isMobile = useContext(MobileContext);

  const openDropdown = useCallback(() => {
    setTimeout(
      () => {
        if (isOpen || disabled) {
          return;
        }
        wasOpen.current = isOpen;
        focusWasInDropdown.current = false;
        setIsOpen(true);
        onOpen && onOpen();
        onToggle && onToggle(true);
        openBiData && logBiEvent(openBiData);
      },
      isMobile && mobileOpenDelay ? 300 : 0
    );
  }, [isMobile, mobileOpenDelay, isOpen, disabled, onOpen, onToggle, openBiData, logBiEvent]);

  const closeDropdown = useCallback(() => {
    if (!isOpen || disabled || forceOpen) {
      return;
    }
    wasOpen.current = isOpen;
    focusWasInDropdown.current =
      !!menuRef.current && menuRef.current.contains(document.activeElement);
    setIsOpen(false);
    onClose && onClose();
    onToggle && onToggle(false);
    closeBiData && logBiEvent(closeBiData);
  }, [isOpen, disabled, forceOpen, menuRef, onClose, onToggle, closeBiData, logBiEvent]);

  const toggleDropdown = useCallback(() => {
    if (isOpen) {
      closeDropdown();
    } else {
      openDropdown();
    }
  }, [closeDropdown, isOpen, openDropdown]);

  // set focus after state change
  useLayoutEffectWithoutSSRComplaining(() => {
    if (isOpen && !wasOpen.current) {
      // focus menu
      if (menuRef.current) {
        menuRef.current.focus({ preventScroll: true });
      }
    }
    if (!isOpen && wasOpen.current) {
      // focus toggle
      if (focusWasInDropdown.current && toggleRef.current) {
        toggleRef.current.focus({ preventScroll: true });
      }
    }
  }, [isOpen]);

  useDebugValue(isOpen ? 'Open' : 'Closed');

  return {
    isOpen,
    openOnHover,
    closeOnLeave,
    openDropdown,
    closeDropdown,
    toggleDropdown,
  };
}
